import React, { useState, useEffect } from 'react';
import { 
  LogIn,
  Users, 
  Store, 
  Camera, 
  ShoppingBag, 
  Clock, 
  Zap,
  Bell,
  Search,
  RefreshCw,
  Eye,
  MessageCircle,
  Navigation,
  CheckCircle,
  XCircle,
  Coffee,
  Activity,
  ChevronRight,
  TrendingUp,
  MapPin,
  TrendingDown
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart as RechartsPieChart, 
  Pie, 
  Cell,
  BarChart,
  Bar
} from 'recharts';
import { FSR_DATA, ACTIVITY_LOG } from '../data/operationsData';
import { FSRDetailPanel } from '../components/FSRDetailPanel';
import { Portal } from '../types';

export const Operations: React.FC<{ portal: Portal, onNavigate: (page: string) => void }> = ({ portal, onNavigate }) => {
  const [activeTab, setOpsTab] = useState<'map' | 'performance' | 'activity'>('map');
  const [selectedFSRId, setSelectedFSRId] = useState<number | null>(null);
  const [fsrPanelOpen, setFsrPanelOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFSRs, setFilteredFSRs] = useState(FSR_DATA);
  const [kpiCounters, setKpiCounters] = useState({ active: 0, visits: 0, photos: 0, bookings: 0, pending: 0 });

  const isMfr = portal === 'manufacturer';
  const portalFSRs = isMfr ? FSR_DATA : FSR_DATA.filter(f => f.distributor.includes('Rajesh'));

  useEffect(() => {
    // KPI Animation
    const duration = 1200;
    const startTime = performance.now();
    
    const animate = (time: number) => {
      const progress = Math.min((time - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      setKpiCounters({
        active: Math.floor(easeOutQuart * (isMfr ? 9 : 4)), // Simplified scaling
        visits: Math.floor(easeOutQuart * (isMfr ? 47 : 22)),
        photos: Math.floor(easeOutQuart * (isMfr ? 38 : 18)),
        bookings: Math.floor(easeOutQuart * (isMfr ? 38 : 18)),
        pending: Math.floor(easeOutQuart * (isMfr ? 2 : 1))
      });

      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isMfr]);

  useEffect(() => {
    const filtered = portalFSRs.filter(fsr => 
      fsr.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fsr.emp.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fsr.distributor.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredFSRs(filtered);
  }, [searchQuery, portalFSRs]);

  const handleRefresh = () => {
    setIsSyncing(true);
    setTimeout(() => setIsSyncing(false), 1500);
  };

  const openFSR = (id: number) => {
    setSelectedFSRId(id);
    setFsrPanelOpen(true);
  };

  const getLatPos = (lat: number | null) => {
    if (!lat) return '50%';
    return Math.min(Math.max(96 - ((lat - 19.05) / 0.21) * 88, 2), 97) + '%';
  };

  const getLngPos = (lng: number | null) => {
    if (!lng) return '50%';
    return Math.min(Math.max(((lng - 72.82) / 0.09) * 88 + 4, 2), 97) + '%';
  };

  const renderKPIs = () => (
    <div className="grid grid-cols-5 gap-4 mb-5">
      {[
        { label: 'Active FSRs', value: kpiCounters.active, sub: '3 idle, 0 not started', icon: Users, grad: 'from-[#4F46E5] to-[#7C3AED]' },
        { label: 'Visits Today', value: kpiCounters.visits, sub: '₹9.2L total value', icon: Store, grad: 'from-[#10B981] to-[#059669]' },
        { label: 'Photos Verified', value: kpiCounters.photos, sub: '9 missing ⚠', icon: Camera, grad: 'from-[#0891B2] to-[#0369A1]' },
        { label: 'Bookings', value: kpiCounters.bookings, sub: '₹9.2L captured', icon: ShoppingBag, grad: 'from-[#F59E0B] to-[#D97706]' },
        { label: 'Pending Approval', value: kpiCounters.pending, sub: 'Approve Now →', icon: Clock, grad: 'from-[#EF4444] to-[#DC2626]', action: () => setOpsTab('map') },
      ].map((kpi, i) => (
        <div key={i} className="bg-white rounded-xl p-4 flex items-center gap-3 border border-[#F1F5F9] shadow-sm">
          <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${kpi.grad} flex items-center justify-center text-white shrink-0`}>
            <kpi.icon size={20} />
          </div>
          <div>
            <div className="text-2xl font-black text-[#0F172A] leading-none">{kpi.value}</div>
            <div className="text-[13px] text-[#64748B] mt-0.5">{kpi.label}</div>
            <div 
              className={`text-[11px] mt-0.5 font-medium ${kpi.action ? 'text-indigo-600 cursor-pointer underline' : 'text-[#94A3B8]'}`}
              onClick={kpi.action}
            >
              {kpi.sub}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col h-screen bg-[#F8FAFC] overflow-hidden page-fade-in font-sans">
      {/* TOPBAR */}
      <header className="h-16 bg-white border-b border-[#F1F5F9] px-6 flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A] tracking-tight">Operations</h1>
          <p className="text-[12px] font-medium text-[#94A3B8]">Live field tracking & performance</p>
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2 px-3 py-1 bg-emerald-50 text-[#059669] rounded-full border border-emerald-100">
             <div className="w-2 h-2 rounded-full bg-[#059669] animate-pulse" />
             <span className="text-[13px] font-semibold tracking-tight uppercase tracking-widest leading-none">● Live · 7 Apr</span>
          </div>
          <div className="flex items-center gap-3">
             <button className="w-10 h-10 rounded-full hover:bg-slate-50 flex items-center justify-center text-slate-400 relative">
                <Bell size={20} />
                <div className="absolute top-2 right-2 w-2 h-2 bg-rose-500 rounded-full border-2 border-white" />
             </button>
             <div className="w-9 h-9 rounded-full bg-indigo-100 border-2 border-white shadow-sm flex items-center justify-center text-indigo-600 font-bold text-xs uppercase italic">AD</div>
          </div>
        </div>
      </header>

      {/* CONTENT SCROLL AREA */}
      <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
        {renderKPIs()}

        {/* TAB BAR */}
        <div className="flex gap-1 bg-[#F1F5F9] p-1 rounded-xl w-fit mb-5">
          {[
            { id: 'map', label: '📍 Live Map & Tracking' },
            { id: 'performance', label: '👤 FSR Performance' },
            { id: 'activity', label: '📋 Activity Log' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setOpsTab(tab.id as any)}
              className={`h-10 px-5 rounded-lg text-[14px] font-semibold transition-all duration-200
                ${activeTab === tab.id 
                  ? 'bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] text-white shadow-lg shadow-indigo-100' 
                  : 'text-[#94A3B8] hover:text-[#64748B]'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'map' && (
            <motion.div 
              key="map" 
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="grid grid-cols-3 gap-4"
            >
              {/* LEFT & CENTER */}
              <div className="col-span-2 flex flex-col gap-4">
                {/* MAP CARD */}
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#F1F5F9] flex flex-col">
                  <div className="px-5 py-4 border-b border-[#F1F5F9] flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-[#0F172A]">Live Field Map</h3>
                      <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 text-[#059669] rounded-full text-[10px] font-bold uppercase">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#059669] animate-pulse" />
                        9 FSRs Active
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                       <button className="h-8 px-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg text-[11px] font-bold text-[#64748B] hover:bg-[#F1F5F9]">All FSRs ▾</button>
                       <button onClick={handleRefresh} className="w-8 h-8 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center text-[#64748B] hover:text-indigo-600">
                         <RefreshCw size={14} className={isSyncing ? 'animate-spin' : ''} />
                       </button>
                    </div>
                  </div>
                  
                  <div className="h-[420px] bg-[#1E293B] relative overflow-hidden group cursor-grab active:cursor-grabbing">
                    {/* CSS ROADS */}
                    {[5,11,18,25,33,41,49,57,65,73,81,90].map(y => (
                      <div key={y} className="absolute left-0 right-0 h-[1.5px] bg-[#2D3E55]" style={{ top: `${y}%` }} />
                    ))}
                    {[7,17,27,37,47,57,67,77,87,95].map(x => (
                      <div key={x} className="absolute top-0 bottom-0 w-[1.5px] bg-[#2D3E55]" style={{ left: `${x}%` }} />
                    ))}
                    {/* CITY BLOCKS */}
                    {[{t:6,l:8,w:8,h:4},{t:6,l:28,w:9,h:4},{t:6,l:58,w:7,h:4},{t:19,l:18,w:7,h:5},{t:19,l:48,w:8,h:5},{t:19,l:78,w:8,h:5},{t:34,l:8,w:9,h:4},{t:34,l:38,w:7,h:5},{t:34,l:68,w:8,h:4},{t:50,l:18,w:8,h:4},{t:50,l:58,w:7,h:4},{t:66,l:28,w:9,h:5}].map((block, i) => (
                      <div key={i} className="absolute bg-[#2D3E55]/40 rounded-sm" style={{ top: `${block.t}%`, left: `${block.l}%`, width: `${block.w}%`, height: `${block.h}%` }} />
                    ))}

                    {/* FSR MARKERS */}
                    {portalFSRs.filter(f => f.lat !== null).map(fsr => (
                      <div 
                        key={fsr.id} 
                        className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
                        style={{ top: getLatPos(fsr.lat), left: getLngPos(fsr.lng) }}
                        onClick={() => openFSR(fsr.id)}
                      >
                        <div className="relative group/marker cursor-pointer">
                          {fsr.currentStatus === 'visiting' && (
                            <div className="absolute inset-0 -m-1.5 w-[52px] h-[52px] border-2 rounded-full animate-ping opacity-20" style={{ borderColor: fsr.color }} />
                          )}
                          <div 
                            className="w-10 h-10 rounded-full border-[2.5px] border-white shadow-xl flex items-center justify-center text-white font-bold text-[11px] relative"
                            style={{ backgroundColor: fsr.color }}
                          >
                            {fsr.avatar}
                            <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white
                              ${fsr.currentStatus === 'visiting' ? 'bg-[#10B981]' : fsr.currentStatus === 'traveling' ? 'bg-[#3B82F6]' : 'bg-[#F59E0B]'}`} 
                            />
                          </div>
                          
                          {/* NAME TAG */}
                          <div className="absolute top-[110%] left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-sm rounded-md px-2 py-0.5 whitespace-nowrap shadow-md border border-[#F1F5F9]">
                            <span className="text-[9px] font-black text-[#0F172A] uppercase tracking-tight italic">{fsr.name.split(' ')[0]}</span>
                          </div>

                          {/* TOOLTIP */}
                          <div className="absolute bottom-[130%] left-1/2 -translate-x-1/2 bg-white rounded-xl p-4 min-w-[230px] shadow-2xl border border-[#F1F5F9] opacity-0 group-hover/marker:opacity-100 translate-y-1 group-hover/marker:translate-y-0 transition-all pointer-events-none z-30">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-[10px]" style={{ backgroundColor: fsr.color }}>{fsr.avatar}</div>
                              <div>
                                <div className="text-[13px] font-bold text-[#0F172A] uppercase italic leading-none">{fsr.name}</div>
                                <div className="text-[10px] text-[#94A3B8] font-mono mt-0.5">{fsr.emp}</div>
                              </div>
                              <div className={`ml-auto px-1.5 py-0.5 rounded-full text-[8px] font-black uppercase
                                ${fsr.currentStatus === 'visiting' ? 'bg-[#ECFDF5] text-[#059669]' : 'bg-[#EFF6FF] text-[#3B82F6]'}`}>
                                {fsr.currentStatus}
                              </div>
                            </div>
                            <div className="text-[11px] text-[#64748B] flex items-center gap-1.5 mt-2">
                              <MapPin size={12} /> {fsr.currentStore || 'Not at store'}
                            </div>
                            <div className="grid grid-cols-2 gap-2 mt-3 p-2 bg-[#F8FAFC] rounded-lg">
                              <div>
                                <div className="text-[9px] text-[#94A3B8] uppercase font-bold tracking-widest">Progress</div>
                                <div className="text-[12px] font-bold text-[#0F172A]">{fsr.todayDone}/{fsr.todayPlan} Stores</div>
                              </div>
                              <div className="text-right">
                                <div className="text-[9px] text-[#94A3B8] uppercase font-bold tracking-widest">Bookings</div>
                                <div className="text-[12px] font-bold text-[#059669]">₹{fsr.todayValue.toLocaleString()}</div>
                              </div>
                            </div>
                            <div className="mt-2 text-[10px] font-bold text-indigo-600 flex justify-between">
                               <span>🔥 {fsr.streak} day streak</span>
                               <span>Rank #{fsr.rank}</span>
                            </div>
                            <div className="mt-3 pt-2 border-t border-[#F1F5F9] text-center">
                              <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest italic">Click to view full timeline →</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* LEGEND */}
                    <div className="absolute bottom-4 left-4 bg-black/80 backdrop-blur-md rounded-xl p-3 border border-white/10 z-20">
                      <div className="flex gap-4 mb-2 border-b border-white/10 pb-2">
                        <div className="flex items-center gap-1.5 text-[9px] font-bold text-white/70 uppercase"><div className="w-2 h-2 rounded-full bg-[#10B981]" /> Visiting</div>
                        <div className="flex items-center gap-1.5 text-[9px] font-bold text-white/70 uppercase"><div className="w-2 h-2 rounded-full bg-[#3B82F6]" /> Traveling</div>
                        <div className="flex items-center gap-1.5 text-[9px] font-bold text-white/70 uppercase"><div className="w-2 h-2 rounded-full bg-[#F59E0B]" /> Idle</div>
                      </div>
                      <div className="flex gap-4">
                        <div className="flex items-center gap-1.5 text-[9px] font-bold text-white/70 uppercase"><div className="w-2 h-2 rounded-full bg-[#EF4444]" /> High priority</div>
                        <div className="flex items-center gap-1.5 text-[9px] font-bold text-white/70 uppercase"><div className="w-2 h-2 rounded-full bg-[#10B981]" /> Low priority</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* VISITS PROGRESS SECTION */}
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#F1F5F9]">
                  <div className="flex justify-between items-center mb-5">
                    <h3 className="font-semibold text-[#0F172A]">Store Visit Progress — Today</h3>
                    <div className="px-3 py-1 bg-slate-50 border border-slate-100 rounded-full text-[11px] font-bold text-[#64748B] uppercase tracking-widest">
                       12 STORES · 47 VISITS PLANNED
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    {[
                      { name: 'Kiran Fashion', fsr: 'Amit Kumar', status: 'visiting', progress: 60, val: '● Active', color: 'bg-indigo-500', photos: true },
                      { name: 'Style Zone', fsr: 'Amit Kumar', status: 'booking', progress: 100, val: '₹50,640', color: 'bg-emerald-500', photos: true },
                      { name: 'Ethnic Empire', fsr: 'Priya Desai', status: 'booking', progress: 100, val: '₹34,200', color: 'bg-emerald-500', photos: true },
                      { name: 'Fashion Hub', fsr: 'Amit Kumar', status: 'no-order', progress: 100, val: 'No Order', color: 'bg-amber-500', photos: true },
                      { name: 'Metro Store', fsr: 'Manish Gupta', status: 'visiting', progress: 60, val: '● Active', color: 'bg-indigo-500', photos: false },
                      { name: 'Westside Express', fsr: 'Amit Kumar', status: 'booking', progress: 100, val: '₹43,776', color: 'bg-emerald-500', photos: true },
                      { name: 'Urban Outfitters', fsr: 'Ravi Sharma', status: 'visiting', progress: 60, val: '● Active', color: 'bg-indigo-500', photos: true },
                      { name: 'Trend Setter', fsr: 'Kavita Patel', status: 'visiting', progress: 60, val: '● Active', color: 'bg-indigo-500', photos: false },
                      { name: 'Royal Collections', fsr: 'Arjun Shah', status: 'traveling', progress: 20, val: '→ En route', color: 'bg-blue-400', photos: false },
                      { name: 'Smart Choice', fsr: 'Sunita Rao', status: 'not-started', progress: 0, val: '—', color: 'bg-slate-200', photos: false },
                    ].map((store, i) => (
                      <div key={i} className="flex items-center gap-4 h-10 border-b border-[#F8FAFC] last:border-0 hover:bg-slate-50/50 px-2 rounded-lg transition-colors group">
                        <div className="w-[140px] text-[13px] font-bold text-[#0F172A] truncate uppercase italic">{store.name}</div>
                        <div className="flex items-center gap-2 w-[120px] shrink-0">
                          <div className="w-5 h-5 rounded bg-indigo-100 flex items-center justify-center text-[8px] font-black text-indigo-600">{store.fsr.charAt(0)}</div>
                          <span className="text-[11px] font-bold text-[#94A3B8] truncate">{store.fsr}</span>
                        </div>
                        <div className="flex-1 h-2 bg-[#F1F5F9] rounded-full overflow-hidden relative">
                           <motion.div 
                             initial={{ width: 0 }} animate={{ width: `${store.progress}%` }} transition={{ duration: 0.8, ease: 'easeOut' }}
                             className={`h-full rounded-full ${store.color}`} 
                           />
                        </div>
                        <div className="w-[100px] text-right font-bold text-[12px] italic tracking-tighter shrink-0" 
                          style={{ color: store.status === 'booking' ? '#059669' : store.status === 'visiting' ? '#4F46E5' : '#94A3B8' }}
                        >
                          {store.val}
                        </div>
                        <div className="w-6 flex justify-center">
                          <Camera size={14} className={store.photos ? 'text-emerald-500' : 'text-slate-200'} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* RIGHT PANEL */}
              <div className="flex flex-col gap-4">
                {/* FSR QUICK LIST */}
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#F1F5F9] flex flex-col">
                  <div className="px-4 py-3 border-b border-[#F1F5F9] flex justify-between items-center">
                    <span className="text-[10px] font-black text-[#4F46E5] uppercase tracking-widest leading-none">FIELD REPS NOW</span>
                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full text-[9px] font-bold uppercase">9 Active</span>
                  </div>
                  <div className="max-h-[360px] overflow-y-auto custom-scrollbar">
                    {portalFSRs.map(fsr => (
                      <div 
                        key={fsr.id} 
                        onClick={() => openFSR(fsr.id)}
                        className="flex items-center gap-3 px-4 py-3 border-b border-[#F8FAFC] hover:bg-[#F5F3FF] cursor-pointer transition-colors group"
                      >
                        <div className="relative shrink-0">
                          <div className="w-[30px] h-[30px] rounded-full flex items-center justify-center text-white font-bold text-[10px] italic" style={{ backgroundColor: fsr.color }}>{fsr.avatar}</div>
                          <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-[1.5px] border-white
                            ${fsr.currentStatus === 'visiting' ? 'bg-[#10B981]' : fsr.currentStatus === 'traveling' ? 'bg-[#3B82F6]' : fsr.currentStatus === 'idle' ? 'bg-[#F59E0B]' : 'bg-[#94A3B8]'}`} 
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-bold text-[#0F172A] truncate uppercase italic leading-none">{fsr.name}</div>
                          <div className={`text-[10px] font-medium mt-1 truncate ${fsr.currentStatus === 'visiting' ? 'text-emerald-600' : 'text-[#94A3B8]'}`}>
                            {fsr.currentStatus === 'visiting' ? `● Visiting ${fsr.currentStore}` : fsr.currentStatus === 'traveling' ? '● En route' : fsr.currentStatus === 'idle' ? '● Idle' : '— Not started'}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[11px] font-black text-indigo-600 italic leading-none tracking-tighter">{fsr.todayDone}/{fsr.todayPlan}</div>
                          <div className="text-[10px] font-bold text-[#94A3B8] mt-1 italic tracking-widest">{fsr.todayValue > 0 ? `₹${(fsr.todayValue/1000).toFixed(0)}K` : '—'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 border-t border-[#F1F5F9]">
                    <button className="w-full h-9 bg-white border border-[#E2E8F0] shadow-sm rounded-lg text-xs font-bold text-[#4F46E5] hover:bg-[#F8FAFC] transition-all">Send Reminder to Idle FSRs</button>
                  </div>
                </div>

                {/* PENDING APPROVALS */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#F1F5F9] border-l-4 border-l-[#F59E0B]">
                   <h4 className="text-[13px] font-black text-[#D97706] mb-4 flex items-center gap-2 uppercase italic tracking-tight italic uppercase">⚡ PENDING APPROVALS (2)</h4>
                   <div className="space-y-4">
                      <div className="flex items-center gap-3 pb-3 border-b border-[#FEF3C7]">
                         <div className="w-8 h-8 rounded-full bg-[#4F46E5] text-white flex items-center justify-center font-bold text-[10px] italic shrink-0">SZ</div>
                         <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-bold text-[#0F172A] truncate uppercase italic leading-none">Style Zone</div>
                            <div className="text-[10px] text-[#94A3B8] mt-1 font-mono uppercase tracking-widest">AMIT · BK-0407-001</div>
                         </div>
                         <div className="text-right shrink-0">
                            <div className="text-[14px] font-black text-[#059669] italic tracking-tighter">₹50,640</div>
                            <button className="mt-1 h-7 px-3 bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-lg text-[10px] font-bold uppercase italic shadow-md shadow-emerald-100">✅ Approve</button>
                         </div>
                      </div>
                      <div className="flex items-center gap-3">
                         <div className="w-8 h-8 rounded-full bg-[#7C3AED] text-white flex items-center justify-center font-bold text-[10px] italic shrink-0">EE</div>
                         <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-bold text-[#0F172A] truncate uppercase italic leading-none">Ethnic Empire</div>
                            <div className="text-[10px] text-[#94A3B8] mt-1 font-mono uppercase tracking-widest">PRIYA · BK-0407-002</div>
                         </div>
                         <div className="text-right shrink-0">
                            <div className="text-[14px] font-black text-[#059669] italic tracking-tighter">₹34,200</div>
                            <button className="mt-1 h-7 px-3 bg-gradient-to-r from-[#10B981] to-[#059669] text-white rounded-lg text-[10px] font-bold uppercase italic shadow-md shadow-emerald-100">✅ Approve</button>
                         </div>
                      </div>
                   </div>
                </div>

                {/* PHOTO STATUS */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#F1F5F9]">
                  <span className="text-[10px] font-black text-[#4F46E5] uppercase tracking-[0.1em] leading-none mb-4 block">PHOTO VERIFICATION</span>
                  <div className="h-[120px] relative flex items-center justify-center mb-4">
                     <ResponsiveContainer width={160} height={120}>
                        <RechartsPieChart>
                           <Pie data={[{value: 38, fill: '#10B981'}, {value: 9, fill: '#FEF2F2'}]} innerRadius={35} outerRadius={55} dataKey="value" stroke="none">
                              <Cell fill="#10B981" />
                              <Cell fill="#FEF2F2" stroke="#F59E0B" strokeWidth={2} />
                           </Pie>
                        </RechartsPieChart>
                     </ResponsiveContainer>
                     <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-[20px] font-black text-[#0F172A] leading-none italic">47</span>
                        <span className="text-[9px] font-bold text-[#94A3B8] uppercase tracking-widest">TOTAL</span>
                     </div>
                  </div>
                  <div className="flex justify-around mb-4 border-t border-[#F8FAFC] pt-3">
                     <div className="text-center">
                        <div className="text-[13px] font-bold text-[#059669] italic">38 Verified ✓</div>
                     </div>
                     <div className="text-center">
                        <div className="text-[13px] font-bold text-[#D97706] italic">9 Missing ⚠</div>
                     </div>
                  </div>
                  <div className="space-y-3">
                     {[{name:'Royal Collections', rep:'Arjun'},{name:'Metro Store', rep:'Manish'},{name:'Smart Choice', rep:'Sunita'}].map((store, i) => (
                       <div key={i} className="flex justify-between items-center h-9 border-b border-[#F8FAFC] last:border-0 hover:bg-slate-50 rounded px-1 group cursor-pointer transition-all">
                          <div className="flex-1 min-w-0">
                            <span className="text-[12px] font-bold text-[#0F172A] uppercase italic leading-none">{store.name}</span>
                            <span className="text-[9px] text-[#94A3B8] block font-bold uppercase tracking-widest mt-0.5">{store.rep}</span>
                          </div>
                          <button className="text-[11px] font-bold text-[#4F46E5] hover:underline uppercase italic">Remind ↗</button>
                       </div>
                     ))}
                  </div>
                  <button className="w-full text-center mt-3 text-[11px] font-black text-indigo-600 uppercase italic tracking-widest hover:underline">View All Missing →</button>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'performance' && (
            <motion.div 
              key="performance" 
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="grid grid-cols-3 gap-4"
            >
              <div className="col-span-2 space-y-4">
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#F1F5F9]">
                  <div className="px-5 py-4 border-b border-[#F1F5F9] flex justify-between items-center">
                    <h3 className="font-semibold text-[#0F172A]">FSR Performance — Today</h3>
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={14} />
                        <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search FSRs..." className="h-9 pl-9 pr-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg text-xs font-semibold focus:border-indigo-500 outline-none w-48" />
                      </div>
                      <button className="h-9 px-4 bg-white border border-[#E2E8F0] shadow-sm rounded-lg text-xs font-bold text-[#64748B] hover:bg-[#F8FAFC] flex items-center gap-2">Export ⬇</button>
                    </div>
                  </div>

                  <div className="px-5 py-3 border-b border-[#F1F5F9] flex gap-2">
                    {['All (12)', 'Active (9)', 'Not Started (2)', 'Suspended (1)'].map((filter, i) => (
                      <button key={i} className={`h-8 px-4 rounded-full text-[11px] font-bold uppercase tracking-tight
                        ${i === 0 ? 'bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] text-white shadow-md' : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:border-indigo-400'}`}>
                        {filter}
                      </button>
                    ))}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-[#F8FAFC] h-11 border-b border-[#E2E8F0]">
                          <th className="px-6 text-[10px] font-black text-[#94A3B8] uppercase tracking-widest">Field Rep</th>
                          <th className="px-5 text-[10px] font-black text-[#94A3B8] uppercase tracking-widest text-center">Status</th>
                          <th className="px-5 text-[10px] font-black text-[#94A3B8] uppercase tracking-widest text-center">Stores</th>
                          <th className="px-5 text-[10px] font-black text-[#94A3B8] uppercase tracking-widest text-center">Bookings</th>
                          <th className="px-5 text-[10px] font-black text-[#94A3B8] uppercase tracking-widest text-right">Value</th>
                          <th className="px-5 text-[10px] font-black text-[#94A3B8] uppercase tracking-widest text-center">Photos</th>
                          <th className="px-2 text-right"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFSRs.map((fsr, i) => (
                          <tr key={fsr.id} onClick={() => openFSR(fsr.id)} className="h-[64px] border-b border-[#F8FAFC] hover:bg-[#F5F3FF] cursor-pointer transition-colors group">
                            <td className="px-6">
                              <div className="flex items-center gap-3">
                                <div className="relative shrink-0">
                                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-[11px] italic" style={{ backgroundColor: fsr.color }}>{fsr.avatar}</div>
                                  <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white
                                    ${fsr.currentStatus === 'visiting' ? 'bg-[#10B981]' : fsr.currentStatus === 'traveling' ? 'bg-[#3B82F6]' : fsr.status === 'inactive' ? 'bg-[#94A3B8]' : 'bg-[#EF4444]'}`} />
                                </div>
                                <div>
                                  <div className="text-[14px] font-bold text-[#0F172A] leading-none uppercase italic">{fsr.name}</div>
                                  <div className="text-[11px] font-mono text-[#94A3B8] mt-1">{fsr.emp}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 text-center">
                              <div className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase italic inline-flex items-center gap-1.5
                                ${fsr.currentStatus === 'visiting' ? 'bg-[#ECFDF5] text-[#059669]' : fsr.currentStatus === 'traveling' ? 'bg-[#EFF6FF] text-[#3B82F6]' : fsr.status === 'inactive' ? 'bg-[#F1F5F9] text-[#64748B]' : 'bg-[#FEF2F2] text-[#EF4444]'}`}>
                                <div className={`w-1.5 h-1.5 rounded-full bg-current ${fsr.currentStatus === 'visiting' ? 'animate-pulse' : ''}`} />
                                {fsr.currentStatus}
                              </div>
                            </td>
                            <td className="px-5 text-center">
                               <div className="text-[16px] font-black text-[#0F172A] italic leading-tight">{fsr.todayDone}<span className="text-[12px] text-[#94A3B8] font-bold">/{fsr.todayPlan}</span></div>
                               <div className="w-12 h-1 bg-[#F1F5F9] rounded-full mx-auto mt-1 overflow-hidden">
                                 <motion.div initial={{ width: 0 }} whileInView={{ width: `${(fsr.todayDone/Math.max(1,fsr.todayPlan))*100}%` }} transition={{ duration: 0.8 }} className="h-full bg-indigo-500 rounded-full" />
                               </div>
                            </td>
                            <td className="px-5 text-center text-[15px] font-black text-[#0F172A] italic">{fsr.todayBookings || '—'}</td>
                            <td className="px-5 text-right text-[14px] font-black text-[#059669] italic">{fsr.todayValue > 0 ? `₹${fsr.todayValue.toLocaleString()}` : '—'}</td>
                            <td className="px-5 text-center">
                               <div className="flex items-center justify-center gap-2">
                                 <Camera size={14} className={fsr.todayPhotos > 0 ? 'text-emerald-500' : 'text-slate-300'} />
                                 <span className={`text-[13px] font-black italic ${fsr.todayPhotos > 0 ? 'text-emerald-500' : 'text-slate-300'}`}>{fsr.todayPhotos || '—'}</span>
                               </div>
                            </td>
                            <td className="px-2 pr-6">
                              <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button className="w-8 h-8 rounded-lg bg-white border border-[#E2E8F0] flex items-center justify-center text-[#4F46E5] hover:bg-[#EEF2FF] shadow-sm"><Eye size={14} /></button>
                                <button className="w-8 h-8 rounded-lg bg-white border border-[#E2E8F0] flex items-center justify-center text-emerald-600 hover:bg-[#ECFDF5] shadow-sm"><MessageCircle size={14} /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-6 py-4 bg-[#FAFBFF] border-t border-[#F1F5F9] flex justify-between items-center text-[12px] text-[#94A3B8]">
                     <span>Showing 12 field reps</span>
                     <span className="font-bold text-[#0F172A] italic tracking-tight">Team today: 47 visits · 38 bookings · ₹9.2L</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#F1F5F9]">
                  <span className="text-[11px] font-black text-[#4F46E5] uppercase tracking-widest block mb-4 italic">🏆 TODAY'S LEADERBOARD</span>
                  <div className="space-y-1">
                    {[...portalFSRs].sort((a,b) => b.todayValue - a.todayValue).slice(0,5).map((fsr, i) => (
                      <div 
                        key={fsr.id} onClick={() => openFSR(fsr.id)}
                        className="flex items-center gap-3 h-[52px] border-b border-[#F8FAFC] last:border-0 hover:bg-[#F5F3FF] cursor-pointer rounded-lg px-2 group transition-all"
                      >
                         <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white font-black text-[11px] shrink-0
                           ${i === 0 ? 'bg-[#F59E0B]' : i === 1 ? 'bg-[#94A3B8]' : i === 2 ? 'bg-[#D97706]' : 'bg-[#F1F5F9] text-[#94A3B8]'}`}>
                           {i + 1}
                         </div>
                         <div className="w-[28px] h-[28px] rounded-full flex items-center justify-center text-white font-bold text-[9px] italic shrink-0" style={{ backgroundColor: fsr.color }}>{fsr.avatar}</div>
                         <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-bold text-[#0F172A] truncate uppercase italic leading-none">{fsr.name}</div>
                            <div className="text-[10px] text-[#94A3B8] font-bold uppercase tracking-widest mt-1 italic">{fsr.todayBookings} bookings</div>
                         </div>
                         <div className="text-right shrink-0">
                           <div className="text-[14px] font-black text-[#059669] italic tracking-tighter">₹{(fsr.todayValue/1000).toFixed(0)}K</div>
                         </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#F1F5F9]">
                  <span className="text-[11px] font-black text-[#4F46E5] uppercase tracking-widest block mb-4 italic">ATTENDANCE — TODAY</span>
                  <div className="space-y-1 max-h-[300px] overflow-y-auto custom-scrollbar">
                     {portalFSRs.map(fsr => (
                       <div key={fsr.id} className="flex justify-between items-center h-9 border-b border-[#F8FAFC] last:border-0">
                          <div className="flex items-center gap-2">
                             <div className="w-[22px] h-[22px] rounded-full flex items-center justify-center text-white font-bold text-[8px] italic" style={{ backgroundColor: fsr.color }}>{fsr.avatar}</div>
                             <span className="text-[12px] font-bold text-[#0F172A] uppercase italic leading-none">{fsr.name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                             <span className={`text-[11px] font-black italic uppercase ${fsr.checkIn ? 'text-emerald-500' : 'text-rose-500'}`}>{fsr.checkIn || 'Absent'}</span>
                             <span className="text-[10px] font-bold text-[#94A3B8] italic">{fsr.todayDone} stores</span>
                          </div>
                       </div>
                     ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-[#F1F5F9]">
                    <div className="flex justify-between items-center mb-2">
                       <span className="text-[12px] font-black text-emerald-600 italic">9 of 12 checked in</span>
                       <span className="text-[12px] font-black text-[#0F172A] italic">75%</span>
                    </div>
                    <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                       <motion.div initial={{ width: 0 }} whileInView={{ width: '75%' }} className="h-full bg-gradient-to-r from-emerald-500 to-teal-500" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#F1F5F9] border-l-4 border-l-[#EF4444]">
                   <h4 className="text-[14px] font-black text-[#EF4444] mb-2 flex items-center gap-2 uppercase italic tracking-tight italic uppercase italic uppercase">⚠ UNVISITED STORES</h4>
                   <p className="text-[11px] font-bold text-[#94A3B8] uppercase tracking-widest mb-3 italic">3 stores not yet visited today</p>
                   <div className="space-y-3">
                      {[
                        { name: 'Raymond Showroom', area: 'Linking Rd', priority: 'high', fsr: 'Rohan Verma' },
                        { name: 'Kalyan Apparels', area: 'Borivali', priority: 'medium', fsr: 'Deepa Nair' },
                        { name: 'V-One Store', area: 'Colaba', priority: 'low', fsr: 'Rohan Verma' }
                      ].map((store, i) => (
                         <div key={i} className="flex items-center gap-3 pb-3 border-b border-[#FEF2F2] last:border-0 last:pb-0">
                            <div className={`w-2 h-2 rounded-full shrink-0 ${store.priority === 'high' ? 'bg-rose-500' : store.priority === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                            <div className="flex-1 min-w-0">
                               <div className="text-[13px] font-bold text-[#0F172A] truncate uppercase italic leading-none">{store.name}</div>
                               <div className="text-[10px] text-[#94A3B8] font-bold uppercase tracking-widest mt-1 italic">{store.area} · <span className="text-[#64748B]">{store.fsr}</span></div>
                            </div>
                            <button className="text-[11px] font-black text-rose-500 italic hover:underline uppercase tracking-tight italic">Remind ↗</button>
                         </div>
                      ))}
                   </div>
                   <button className="w-full h-9 bg-rose-50 border border-rose-100 rounded-lg text-xs font-bold text-rose-600 mt-4 hover:bg-rose-100 transition-all uppercase italic tracking-widest">Send Team Reminder</button>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'activity' && (
            <motion.div 
              key="activity" 
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="grid grid-cols-3 gap-6"
            >
              <div className="col-span-2 space-y-4">
                <div className="bg-white rounded-xl p-3.5 flex gap-3 flex-wrap shadow-sm border border-[#F1F5F9]">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={15} />
                    <input type="text" placeholder="Search activity..." className="w-full h-10 pl-10 pr-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-[13px] font-semibold focus:border-indigo-500 outline-none" />
                  </div>
                  <button className="h-10 px-4 bg-white border border-[#E2E8F0] rounded-xl text-[13px] font-bold text-[#64748B] hover:bg-[#F8FAFC]">All FSRs ▾</button>
                  <button className="h-10 px-4 bg-white border border-[#E2E8F0] rounded-xl text-[13px] font-bold text-[#64748B] hover:bg-[#F8FAFC]">All Events ▾</button>
                  <button className="h-10 px-4 bg-white border border-[#E2E8F0] rounded-xl text-[13px] font-bold text-[#64748B] hover:bg-[#F8FAFC]">Export ⬇</button>
                </div>

                <div className="flex gap-2 mb-2">
                  {['All (23)', 'Visits', 'Bookings', 'Photos', 'No Orders', 'Approvals', 'Check-ins'].map((f, i) => (
                    <button key={i} className={`h-8 px-4 rounded-full text-[11px] font-black uppercase tracking-tight italic
                      ${i === 0 ? 'bg-gradient-to-br from-[#4F46E5] to-[#7C3AED] text-white shadow-md' : 'bg-white border border-[#E2E8F0] text-[#64748B]'}`}>
                      {f}
                    </button>
                  ))}
                </div>

                <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#F1F5F9]">
                  <div className="px-5 py-4 border-b border-[#F1F5F9] flex justify-between items-center bg-[#FAFBFF]">
                    <h3 className="text-[15px] font-black text-[#0F172A] italic uppercase italic uppercase">Activity Log — 7 Apr 2024</h3>
                    <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black uppercase tracking-widest">23 EVENTS</span>
                  </div>
                  
                  <div className="px-5 relative">
                    <div className="absolute left-[36px] top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#4F46E5] via-[#E2E8F0] to-[#F1F5F9]" />
                    
                    {ACTIVITY_LOG.map((event, i) => (
                      <motion.div 
                        key={event.id}
                        initial={{ opacity: 0, x: -10 }} 
                        whileInView={{ opacity: 1, x: 0 }} 
                        transition={{ delay: i * 0.03 }}
                        className="flex items-center gap-4 py-4 border-b border-[#F8FAFC] last:border-0 hover:bg-[#F9FAFB] transition-colors relative"
                      >
                        <div className="w-[64px] text-right text-[12px] font-medium text-[#94A3B8] shrink-0 uppercase tracking-tighter">{event.time}</div>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white shrink-0 z-10 border-2 border-white shadow-sm" style={{ backgroundColor: event.color }}>
                          <event.icon size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                           <div className="text-[14px] font-bold text-[#0F172A] uppercase italic leading-none">{event.label}</div>
                           <div className="text-[12px] text-[#64748B] mt-1 italic tracking-tight">{event.detail}</div>
                        </div>
                        <div className="shrink-0 flex items-center gap-2 bg-[#F1F5F9] px-2.5 py-1.5 rounded-full border border-slate-100 italic">
                           <div className="w-4 h-4 rounded bg-indigo-100 flex items-center justify-center text-[7px] font-black text-indigo-600">{event.fsr.charAt(0)}</div>
                           <span className="text-[10px] font-black text-[#0F172A] uppercase tracking-tighter italic">{event.fsr}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  <div className="p-5">
                     <button className="w-full h-11 bg-[#F8FAFC] border border-[#F1F5F9] rounded-xl text-[13px] font-black text-[#4F46E5] uppercase italic tracking-[0.1em] hover:bg-[#EEEFFF]">Load More Content ↓</button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                 <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#F1F5F9]">
                    <span className="text-[10px] font-black text-[#4F46E5] uppercase tracking-[0.1em] block mb-4 italic">TODAY'S ACTIVITY SUMMARY</span>
                    <div className="space-y-4">
                       {[
                         { icon: LogIn, label: 'Login/Check-in', count: 9, color: '#10B981' },
                         { icon: MapPin, label: 'Store Arrivals', count: 8, color: '#4F46E5' },
                         { icon: Camera, label: 'Photos Captured', count: 8, color: '#0891B2' },
                         { icon: ShoppingBag, label: 'Bookings Created', count: 6, color: '#10B981' },
                         { icon: XCircle, label: 'No Orders', count: 1, color: '#F59E0B' },
                         { icon: RefreshCw, label: 'WhatsApp Sent', count: 3, color: '#25D366' }
                       ].map((stat, i) => (
                         <div key={i} className="flex items-center gap-3 border-b border-[#F8FAFC] pb-3 last:border-0 last:pb-0">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-white shrink-0" style={{ backgroundColor: stat.color }}>
                               <stat.icon size={13} />
                            </div>
                            <span className="text-[13px] font-bold text-[#0F172A] flex-1 italic truncate">{stat.label}</span>
                            <span className="text-[15px] font-black text-[#0F172A] italic">{stat.count}</span>
                         </div>
                       ))}
                    </div>
                 </div>

                 <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#F1F5F9]">
                    <span className="text-[10px] font-black text-[#4F46E5] uppercase tracking-[0.1em] block mb-4 italic">BY FIELD REP</span>
                    <div className="space-y-3">
                       {portalFSRs.slice(0, 9).map((fsr, i) => (
                         <div key={i} className="flex justify-between items-center h-10 border-b border-[#F8FAFC] last:border-0">
                            <div className="flex items-center gap-2">
                               <div className="w-[26px] h-[26px] rounded-full flex items-center justify-center text-white font-bold text-[10px]" style={{ backgroundColor: fsr.color }}>{fsr.avatar}</div>
                               <span className="text-[12px] font-bold text-[#0F172A] italic truncate max-w-[100px] uppercase leading-none">{fsr.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                               <span className="text-[11px] font-bold text-[#94A3B8] italic">{Math.floor(Math.random() * 5) + 2} events</span>
                               <div className="flex gap-1 shrink-0">
                                 {i % 2 === 0 && <Camera size={11} className="text-emerald-500" />}
                                 {i % 3 === 0 && <ShoppingBag size={11} className="text-indigo-500" />}
                               </div>
                            </div>
                         </div>
                       ))}
                    </div>
                 </div>

                 <div className="bg-[#F0FDF4] rounded-2xl p-4 shadow-sm border border-[#D1FAE5] border-l-4 border-l-[#10B981]">
                    <div className="flex justify-between items-center mb-3">
                       <span className="text-[10px] font-black text-[#059669] uppercase tracking-[0.1em] flex items-center gap-1.5">
                         <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" /> ● LIVE EVENTS
                       </span>
                    </div>
                    <div className="space-y-3">
                       {ACTIVITY_LOG.slice(0, 3).map((event, i) => (
                         <div key={i} className="flex gap-3 pb-3 border-b border-[#D1FAE5] last:border-0 last:pb-0">
                            <div className="w-7 h-7 rounded-xl bg-white flex items-center justify-center text-[#10B981] shrink-0 shadow-sm border border-[#D1FAE5]"><Activity size={13} /></div>
                            <div>
                               <div className="text-[12px] font-black text-[#064E3B] uppercase italic leading-none">{event.label}</div>
                               <div className="text-[10px] text-[#059669] font-bold mt-1 uppercase tracking-tight italic">{event.time} · 1 min ago</div>
                            </div>
                         </div>
                       ))}
                    </div>
                 </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <FSRDetailPanel 
        isOpen={fsrPanelOpen} 
        onClose={() => setFsrPanelOpen(false)} 
        fsrId={selectedFSRId || 1} 
        onNext={() => {
           const idx = portalFSRs.findIndex(f => f.id === (selectedFSRId || 1));
           const nextIdx = (idx + 1) % portalFSRs.length;
           setSelectedFSRId(portalFSRs[nextIdx].id);
        }}
        onPrev={() => {
           const idx = portalFSRs.findIndex(f => f.id === (selectedFSRId || 1));
           const prevIdx = (idx - 1 + portalFSRs.length) % portalFSRs.length;
           setSelectedFSRId(portalFSRs[prevIdx].id);
        }}
      />
    </div>
  );
};
